import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';

interface FormSubmission {
  _id: string;
  submissionId: string;
  annonsor: string;
  stillingstittel: string;
  arbeidsgiver: string;
  kampanjenavn: string;
  ordrenumre: string[];
  submittedAt: string;
  emailSent: boolean;
  asanaTaskCreated: boolean;
  pdfGenerated: boolean;
}

interface SubmissionsResponse {
  submissions: FormSubmission[];
  pagination: {
    current: number;
    total: number;
    limit: number;
    totalRecords: number;
  };
}

const MyOrders: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    limit: 20,
    totalRecords: 0
  });
  const [filters, setFilters] = useState({
    annonsor: '',
    stillingstittel: '',
    arbeidsgiver: '',
    fromDate: '',
    toDate: '',
    page: 1
  });

  const fetchMySubmissions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: '20',
        ...(filters.annonsor && { annonsor: filters.annonsor }),
        ...(filters.stillingstittel && { stillingstittel: filters.stillingstittel }),
        ...(filters.arbeidsgiver && { arbeidsgiver: filters.arbeidsgiver }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate })
      });

      const apiUrl = `${process.env.REACT_APP_API_URL}/forms/submissions/my?${queryParams}`;
      console.log('Making request to:', apiUrl);
      const response = await fetch(apiUrl, {
        credentials: 'include'
      });
      console.log('Response status:', response.status, 'Content-Type:', response.headers.get('content-type'));

      if (response.ok) {
        const data: SubmissionsResponse = await response.json();
        console.log('MyOrders: Fetched submissions data:', data);
        setSubmissions(data.submissions);
        setPagination(data.pagination);
      } else {
        console.error('MyOrders: Failed to fetch submissions, status:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('MyOrders: Error response:', errorData);
      }
    } catch (error) {
      console.error('Error fetching my submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMySubmissions();
  }, [filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      annonsor: '',
      stillingstittel: '',
      arbeidsgiver: '',
      fromDate: '',
      toDate: '',
      page: 1
    });
  };

  const viewSubmission = (submissionId: string) => {
    navigate(`/my-orders/${submissionId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('no-NO');
  };

  const getStatusColor = (emailSent: boolean, asanaTaskCreated: boolean, pdfGenerated: boolean) => {
    const successCount = [emailSent, asanaTaskCreated, pdfGenerated].filter(Boolean).length;
    if (successCount === 3) return 'success';
    if (successCount >= 2) return 'warning';
    return 'error';
  };

  return (
    <div className="dashboard-page">
      <Header />
      <div className="dashboard-content">
        <div className="container">
          <div className="page-header">
            <h1>Mine bestillinger</h1>
            <p>Se alle dine innsendte salgsskjemaer</p>
          </div>

          {/* Quick Stats */}
          <div className="quick-stats">
            <div className="stat-card">
              <h3>{pagination.totalRecords}</h3>
              <p>Totale bestillinger</p>
            </div>
            <div className="stat-card">
              <h3>{submissions.filter(s => s.emailSent && s.asanaTaskCreated).length}</h3>
              <p>Vellykkede innsendinger</p>
            </div>
          </div>

          {/* Filters */}
          <div className="filters-section">
            <h3>Filtrer bestillinger</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="annonsor">Annonsør</label>
                <input
                  type="text"
                  id="annonsor"
                  value={filters.annonsor}
                  onChange={(e) => handleFilterChange('annonsor', e.target.value)}
                  placeholder="Søk etter annonsør..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="stillingstittel">Stillingstittel</label>
                <input
                  type="text"
                  id="stillingstittel"
                  value={filters.stillingstittel}
                  onChange={(e) => handleFilterChange('stillingstittel', e.target.value)}
                  placeholder="Søk etter stillingstittel..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="arbeidsgiver">Arbeidsgiver</label>
                <input
                  type="text"
                  id="arbeidsgiver"
                  value={filters.arbeidsgiver}
                  onChange={(e) => handleFilterChange('arbeidsgiver', e.target.value)}
                  placeholder="Søk etter arbeidsgiver..."
                />
              </div>
              <div className="filter-group">
                <label htmlFor="fromDate">Fra dato</label>
                <input
                  type="date"
                  id="fromDate"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="toDate">Til dato</label>
                <input
                  type="date"
                  id="toDate"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                />
              </div>
              <div className="filter-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={clearFilters}
                >
                  Nullstill filtre
                </button>
              </div>
            </div>
          </div>

          {/* Orders List */}
          <div className="orders-section">
            <div className="section-header">
              <h3>Dine bestillinger ({pagination.totalRecords})</h3>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/form')}
              >
                + Ny bestilling
              </button>
            </div>

            {loading ? (
              <div className="loading">Laster bestillinger...</div>
            ) : submissions.length === 0 ? (
              <div className="no-data">
                <p>Du har ingen bestillinger ennå.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/form')}
                >
                  Opprett din første bestilling
                </button>
              </div>
            ) : (
              <>
                <div className="orders-grid">
                  {submissions.map((submission) => (
                    <div key={submission._id} className="order-card">
                      <div className="order-header">
                        <h4>{submission.kampanjenavn}</h4>
                        <span className={`status-indicator ${getStatusColor(submission.emailSent, submission.asanaTaskCreated, submission.pdfGenerated)}`}>
                          {getStatusColor(submission.emailSent, submission.asanaTaskCreated, submission.pdfGenerated) === 'success' ? 'Fullført' : 
                           getStatusColor(submission.emailSent, submission.asanaTaskCreated, submission.pdfGenerated) === 'warning' ? 'Delvis' : 'Feilet'}
                        </span>
                      </div>
                      
                      <div className="order-details">
                        <div className="detail-row">
                          <strong>Annonsør:</strong> {submission.annonsor}
                        </div>
                        <div className="detail-row">
                          <strong>Stillingstittel:</strong> {submission.stillingstittel}
                        </div>
                        <div className="detail-row">
                          <strong>Arbeidsgiver:</strong> {submission.arbeidsgiver}
                        </div>
                        <div className="detail-row">
                          <strong>Ordrenr:</strong> {Array.isArray(submission.ordrenumre) ? submission.ordrenumre.join(', ') : submission.ordrenumre}
                        </div>
                        <div className="detail-row">
                          <strong>Sendt inn:</strong> {formatDate(submission.submittedAt)}
                        </div>
                      </div>

                      <div className="order-status">
                        <div className="status-badges">
                          <span className={`status-badge ${submission.emailSent ? 'success' : 'error'}`}>
                            E-post {submission.emailSent ? '✓' : '✗'}
                          </span>
                          <span className={`status-badge ${submission.asanaTaskCreated ? 'success' : 'error'}`}>
                            Asana {submission.asanaTaskCreated ? '✓' : '✗'}
                          </span>
                          <span className={`status-badge ${submission.pdfGenerated ? 'success' : 'error'}`}>
                            PDF {submission.pdfGenerated ? '✓' : '✗'}
                          </span>
                        </div>
                      </div>

                      <div className="order-actions">
                        <button
                          className="btn btn-primary btn-small"
                          onClick={() => viewSubmission(submission._id)}
                        >
                          Se detaljer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.total > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      disabled={pagination.current === 1}
                      onClick={() => handlePageChange(pagination.current - 1)}
                    >
                      Forrige
                    </button>
                    <span className="pagination-info">
                      Side {pagination.current} av {pagination.total}
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={pagination.current === pagination.total}
                      onClick={() => handlePageChange(pagination.current + 1)}
                    >
                      Neste
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyOrders;